// @ts-check
import { MONTH_SHORT } from './constants';
import { getRateForMonth, getLatestRate } from './exchangeRates';

/**
 * Compute the remaining cost basis for a foreign-currency balance using the
 * weighted-average cost method with support for negative balances ("short" positions)
 * and retroactive lot settlement ("update backwards").
 *
 * @param {{ currency?: string, lots?: Array<{date:string,foreignAmount:number,rate:number}>, spends?: Array<{date:string,foreignAmount:number}> }} balance
 * @param {string} [mainCurrency='ILS']
 * @returns {{ totalForeign: number, totalNis: number, avgRate: number }}
 */
export function computeBalanceCostBasis(balance, mainCurrency = 'ILS') {
  const events = [
    ...(balance?.lots   || []).map(l => ({ ...l, _type: 'buy'   })),
    ...(balance?.spends || []).map(s => ({ ...s, _type: 'spend' })),
  ].sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  let totalForeign = 0;
  let totalNis = 0;
  let lastRate = 0;

  for (const ev of events) {
    const amt = Number(ev.foreignAmount) || 0;
    if (amt <= 0) continue;

    if (ev._type === 'buy') {
      const buyRate = Number(ev.rate) || 0;
      if (buyRate > 0) lastRate = buyRate;

      if (totalForeign >= 0) {
        totalForeign += amt;
        totalNis += amt * buyRate;
      } else {
        // Retroactively settling past deficit ("update backwards")
        const deficit = -totalForeign;
        if (amt <= deficit) {
          totalForeign += amt;
          totalNis += amt * buyRate;
        } else {
          const excess = amt - deficit;
          totalForeign = excess;
          totalNis = excess * buyRate;
        }
      }
    } else {
      if (ev.rate && Number(ev.rate) > 0) {
        lastRate = Number(ev.rate);
      }

      if (totalForeign > 0) {
        const curAvg = totalNis / totalForeign;
        if (amt <= totalForeign) {
          totalForeign -= amt;
          totalNis -= amt * curAvg;
          if (Math.abs(totalForeign) < 1e-9) {
            totalForeign = 0;
            totalNis = 0;
          }
        } else {
          totalForeign -= amt;
          totalNis = 0;
        }
      } else {
        totalForeign -= amt;
      }
    }
  }

  if (Math.abs(totalForeign) < 1e-9) {
    totalForeign = 0;
    totalNis = 0;
  }

  let avgRate = 0;
  if (totalForeign > 0) {
    avgRate = totalNis / totalForeign;
  } else if (totalForeign < 0) {
    const marketRate = balance?.currency
      ? getLatestRate(balance.currency, mainCurrency)
      : null;
    avgRate = (marketRate && marketRate > 0) ? marketRate : (lastRate > 0 ? lastRate : 1);
    totalNis = parseFloat((totalForeign * avgRate).toFixed(2));
  }

  return {
    totalForeign: parseFloat(totalForeign.toFixed(6)),
    totalNis: parseFloat(totalNis.toFixed(2)),
    avgRate: parseFloat(avgRate.toFixed(4)),
  };
}

/**
 * @typedef {{ id: string, name: string, type: 'income'|'expense'|'savings' }} Category
 * @typedef {{ id: string, date: string, type: 'income'|'expense'|'savings', category: string, description: string, amount: number, bucket: string|null, currency?: string, foreignAmount?: number|null, exchangeRate?: number|null, tags?: string[] }} Transaction
 * @typedef {{ id: string, name: string, emoji?: string, group?: string, initialBalance: number, target?: number, monthlyTarget: number, currency?: string, createdDate: string, adjustments: Adjustment[], archived?: boolean }} Bucket
 * @typedef {{ id: string, type: string, amount: number, date: string, note?: string, recurring?: boolean }} Adjustment
 * @typedef {{ id: string, amount: number, overrides?: Record<string, number>, history?: Array<{from: string, amount: number}> }} BudgetItem
 */

/**
 * Resolve a category or yearly-expense id to its display name.
 * Falls back to the raw id string if nothing matches.
 */
export function resolveCategoryName(id, categories, yearlyExpenses = []) {
  const cat = categories.find(c => c.id === id);
  if (cat) return cat.name;
  const ye = yearlyExpenses.find(y => y.id === id);
  return ye ? `📅 ${ye.name}` : id;
}

/**
 * Resolve the effective budget amount for a single budget item and month.
 *
 * Logic (in priority order):
 *   1. Monthly override for the exact month  (if includeOverride)
 *   2. Latest history entry whose `from` ≤ month
 *   3. The item's base `amount`
 *
 * @param {object|undefined} budgetItem - from budgetItems array
 * @param {string}           month      - 'YYYY-MM'
 * @param {{ includeOverride?: boolean }} opts
 * @returns {number}
 */
export function resolveBudgetAmount(budgetItem, month, { includeOverride = true } = {}) {
  if (!budgetItem) return 0;
  if (includeOverride && budgetItem.overrides?.[month] !== undefined) {
    return budgetItem.overrides[month];
  }
  if (budgetItem.history && budgetItem.history.length > 0) {
    const applicable = budgetItem.history.filter(h => h.from <= month);
    if (applicable.length > 0) return applicable[applicable.length - 1].amount;
  }
  return budgetItem.amount ?? 0;
}

/**
 * @param {Transaction[]} transactions
 * @param {'month'|'ytd'|'year'|'prev'|'custom'} period
 * @param {string} [customFrom] - YYYY-MM-DD
 * @param {string} [customTo]   - YYYY-MM-DD
 * @param {number} [selectedYear]
 * @returns {Transaction[]}
 */
export function filterByPeriod(transactions, period, customFrom, customTo, selectedYear) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();

  const todayLocal = `${y}-${String(m + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  let from, to;
  if (period === 'month') {
    const lastDay = new Date(y, m + 1, 0).getDate();
    from = `${y}-${String(m + 1).padStart(2, '0')}-01`;
    to   = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  } else if (period === 'ytd') {
    from = `${y}-01-01`;
    to = todayLocal;
  } else if (period === 'year') {
    const fy = selectedYear || y;
    from = `${fy}-01-01`;
    to = `${fy}-12-31`;
  } else if (period === 'prev') {
    const pm = m === 0 ? 11 : m - 1;
    const py = m === 0 ? y - 1 : y;
    const lastDay = new Date(py, pm + 1, 0).getDate();
    from = `${py}-${String(pm + 1).padStart(2, '0')}-01`;
    to   = `${py}-${String(pm + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  } else if (period === 'custom' && customFrom && customTo) {
    from = customFrom; to = customTo;
  } else {
    from = `${y}-01-01`;
    to = now.toISOString().split('T')[0];
  }

  return transactions.filter(t => t.date >= from && t.date <= to);
}

/** @param {Transaction[]} transactions @param {'income'|'expense'|'savings'} type @returns {number} */
export function sumByType(transactions, type) {
  return transactions.filter(t => t.type === type).reduce((s, t) => s + t.amount, 0);
}

/**
 * All-time available account balance: total income minus expenses minus
 * money moved to savings. Ignores any period filtering.
 * @param {Transaction[]} transactions @returns {number}
 */
export function accountBalance(transactions) {
  return sumByType(transactions, 'income')
       - sumByType(transactions, 'expense')
       - sumByType(transactions, 'savings');
}

/** @param {Transaction[]} transactions @param {Category[]} categories @param {Array} [yearlyExpenses] @returns {Array<{id:string,name:string,value:number}>} */
export function groupByCategory(transactions, categories, yearlyExpenses = []) {
  const map = {};
  transactions.forEach(t => {
    if (!map[t.category]) map[t.category] = 0;
    map[t.category] += t.amount;
  });
  return Object.entries(map).map(([id, value]) => ({
    id,
    name: resolveCategoryName(id, categories, yearlyExpenses),
    value,
  })).sort((a, b) => b.value - a.value);
}

/**
 * Split an actual value vs a target into three stack segments for the
 * Budget vs Actual chart:
 *   - done: achieved up to target = min(actual, target)
 *   - gap:  remaining shortfall   = max(0, target - actual)
 *   - over: overshoot above target = max(0, actual - target)
 * Sum is always max(actual, target), so each stacked bar's height equals
 * whichever side won.
 */
export function budgetVsActualSegments(actual, target) {
  const a = Number(actual)  || 0;
  const t = Number(target)  || 0;
  return {
    done: Math.min(a, t),
    gap:  Math.max(0, t - a),
    over: Math.max(0, a - t),
  };
}

export function getMonthlyData(transactions, year) {
  return MONTH_SHORT.map((name, i) => {
    const prefix = `${year}-${String(i + 1).padStart(2, '0')}`;
    const inc = transactions.filter(t => t.type === 'income'  && t.date.startsWith(prefix)).reduce((s, t) => s + t.amount, 0);
    const exp = transactions.filter(t => t.type === 'expense' && t.date.startsWith(prefix)).reduce((s, t) => s + t.amount, 0);
    const sav = transactions.filter(t => t.type === 'savings' && t.date.startsWith(prefix)).reduce((s, t) => s + t.amount, 0);
    return { name, income: inc, expenses: exp, savings: sav };
  });
}

/**
 * Pick the contribution of a savings transaction to a bucket's balance,
 * expressed in the bucket's own currency.
 *
 *   - Main-currency bucket (bucket.currency missing or === main): use t.amount.
 *   - Foreign bucket, txn was entered in the same foreign currency: use
 *     t.foreignAmount if present, else fall back to t.amount.
 *   - Foreign bucket, txn was in another currency: best-effort — use t.amount
 *     (this is an off-nominal case; the Tracker locks currency to the bucket).
 */
function txnContributionToBucket(t, bucket) {
  if (!bucket.currency) return t.amount;
  if (t.currency && t.currency === bucket.currency && Number.isFinite(t.foreignAmount)) {
    return t.foreignAmount;
  }
  return t.amount;
}

/** @param {Bucket} bucket @param {Transaction[]} transactions @returns {number} */
export function getBucketBalance(bucket, transactions) {
  const txnTotal = transactions
    .filter(t => t.type === 'savings' && t.bucket === bucket.id)
    .reduce((s, t) => s + txnContributionToBucket(t, bucket), 0);
  const adjTotal = (bucket.adjustments || []).reduce((s, a) => s + a.amount, 0);
  return (bucket.initialBalance || 0) + txnTotal + adjTotal;
}

/**
 * "User-contributed" balance — excludes the bucket's initialBalance seed and
 * any type='interest' adjustments. Used on the dashboard so the Savings
 * Buckets total reflects what the user actually deposited, not opening
 * balances or accrued interest.
 */
export function getBucketContributedBalance(bucket, transactions) {
  const txnTotal = transactions
    .filter(t => t.type === 'savings' && t.bucket === bucket.id)
    .reduce((s, t) => s + txnContributionToBucket(t, bucket), 0);
  const adjTotal = (bucket.adjustments || [])
    .filter(a => a.type !== 'interest')
    .reduce((s, a) => s + a.amount, 0);
  return txnTotal + adjTotal;
}

/**
 * Today's bucket balance converted into the app's main currency.
 * Returns null when the bucket is foreign-currency and no FX rate is cached
 * (caller should hide the "approx" display rather than show an unconverted
 * native number with the wrong currency symbol).
 */
export function getBucketBalanceInMain(bucket, transactions, mainCurrency) {
  const native = getBucketBalance(bucket, transactions);
  if (!bucket.currency || bucket.currency === mainCurrency) return native;
  const rate = getLatestRate(bucket.currency, mainCurrency);
  return rate != null ? native * rate : null;
}

/**
 * Main-currency version of getBucketContributedBalance.
 * Returns null for foreign buckets when no FX rate is cached.
 */
export function getBucketContributedBalanceInMain(bucket, transactions, mainCurrency) {
  const native = getBucketContributedBalance(bucket, transactions);
  if (!bucket.currency || bucket.currency === mainCurrency) return native;
  const rate = getLatestRate(bucket.currency, mainCurrency);
  return rate != null ? native * rate : null;
}

/**
 * Compound-aware bucket projection.
 *
 * Backward compatible: pass a number as the third arg to mean `months` (the old
 * call shape `getBucketProjection(b, bal, 24)` keeps working).
 *
 * opts: {
 *   months?,                    // explicit horizon
 *   untilDate?,                 // 'YYYY-MM-DD' — overrides months
 *   annualRate? = 0,            // decimal, e.g. 0.05 for 5% APY (compounded monthly)
 *   monthlyContribution?,       // defaults to bucket.monthlyTarget
 * }
 *
 * If neither months nor untilDate is given AND bucket.target is set, projection
 * runs until the goal is reached (capped at 60 months).
 *
 * Each output entry: { date, name, balance, projectedInterestCumulative }.
 */
export function getBucketProjection(bucket, currentBalance, optsOrMonths = undefined) {
  const opts = typeof optsOrMonths === 'number'
    ? { months: optsOrMonths }
    : (optsOrMonths || {});

  const annualRate = opts.annualRate || 0;
  const monthly = opts.monthlyContribution !== undefined
    ? opts.monthlyContribution
    : (bucket.monthlyTarget || 0);

  const now = new Date();
  const target = bucket.target || 0;

  let maxMonths;
  if (opts.untilDate) {
    const end = new Date(opts.untilDate);
    maxMonths = Math.max(
      1,
      (end.getFullYear() - now.getFullYear()) * 12 + (end.getMonth() - now.getMonth())
    );
  } else if (opts.months) {
    maxMonths = opts.months;
  } else if (target > 0) {
    maxMonths = 60;
  } else {
    maxMonths = 12;
  }

  const result = [];
  let bal = currentBalance;
  let cumInterest = 0;
  const goalCheck = !opts.untilDate && !opts.months && target > 0;

  // Optional main-currency conversion using the latest-known rate held flat
  // forward (future rates are unknown).
  const wantMain = !!opts.convertToMain && bucket.currency && opts.mainCurrency
    && bucket.currency !== opts.mainCurrency;
  const flatRate = wantMain ? getLatestRate(bucket.currency, opts.mainCurrency) : null;

  for (let i = 0; i < maxMonths; i++) {
    const interest = bal * (annualRate / 12);
    bal = bal + interest + monthly;
    cumInterest += interest;
    const d = new Date(now.getFullYear(), now.getMonth() + i + 1, 0);
    const point = {
      date: d.toISOString().slice(0, 10),
      name: `${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`,
      balance: Math.round(bal),
      projectedInterestCumulative: Math.round(cumInterest),
    };
    if (wantMain && flatRate != null) {
      point.balanceMain = Math.round(bal * flatRate);
    }
    result.push(point);
    if (goalCheck && bal >= target) break;
  }

  return result;
}

/**
 * Lookback key → start date.
 * Supports: '1M' | '3M' | '6M' | '1Y' | '2Y' | '5Y' | 'YTD'.
 */
export function lookbackStartDate(key, today = new Date()) {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  switch (key) {
    case '1M':  d.setMonth(d.getMonth() - 1); break;
    case '3M':  d.setMonth(d.getMonth() - 3); break;
    case '6M':  d.setMonth(d.getMonth() - 6); break;
    case '1Y':  d.setFullYear(d.getFullYear() - 1); break;
    case '2Y':  d.setFullYear(d.getFullYear() - 2); break;
    case '5Y':  d.setFullYear(d.getFullYear() - 5); break;
    case 'YTD': return new Date(today.getFullYear(), 0, 1);
    default:    d.setFullYear(d.getFullYear() - 1);
  }
  return d;
}

/**
 * Historical balance time series for a bucket within a lookback window.
 * Walks the merged event stream (initial + savings txns + adjustments) and
 * samples the running balance at the start of the window, every month-end
 * inside it, and today.
 *
 * Returns: [{ date: 'YYYY-MM-DD', balance, name }].
 */
export function getHistoricalBalanceSeries(bucket, transactions, lookbackKey, optsOrNow = {}) {
  // Backward-compat: 4th arg used to be `now: Date`. Accept either.
  const opts = optsOrNow instanceof Date ? { now: optsOrNow } : (optsOrNow || {});
  const now = opts.now || new Date();
  const mainCurrency = opts.mainCurrency;
  const rateOverrides = bucket.rateOverrides || {};
  const todayStr = now.toISOString().slice(0, 10);
  // opts.start (Date|'YYYY-MM-DD') overrides the lookback window for custom ranges.
  const start = opts.start ? new Date(opts.start) : lookbackStartDate(lookbackKey, now);
  const startStr = start.toISOString().slice(0, 10);

  const events = [];
  transactions.forEach(t => {
    if (t.type === 'savings' && t.bucket === bucket.id && t.date <= todayStr) {
      events.push({ date: t.date, amount: txnContributionToBucket(t, bucket) });
    }
  });
  (bucket.adjustments || []).forEach(a => {
    if (a.date <= todayStr) events.push({ date: a.date, amount: a.amount });
  });
  events.sort((a, b) => a.date.localeCompare(b.date));

  const balanceAt = cutoff => {
    let b = bucket.initialBalance || 0;
    for (const e of events) {
      if (e.date <= cutoff) b += e.amount;
      else break;
    }
    return b;
  };

  const isForeign = bucket.currency && mainCurrency && bucket.currency !== mainCurrency;

  const points = [];
  const push = dateStr => {
    if (points.length && points[points.length - 1].date === dateStr) return;
    const d = new Date(dateStr);
    const balance = balanceAt(dateStr);
    const pt = {
      date: dateStr,
      balance: Math.round(balance),
      name: `${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`,
    };
    if (isForeign) {
      const ym = dateStr.slice(0, 7);
      const rate = getRateForMonth(bucket.currency, mainCurrency, ym, {
        override: rateOverrides[ym],
      });
      pt.balanceMain = rate != null ? Math.round(balance * rate) : null;
      pt.rate = rate;
    }
    points.push(pt);
  };

  push(startStr);
  let cursor = new Date(start.getFullYear(), start.getMonth() + 1, 0);
  while (cursor < now) {
    const cs = cursor.toISOString().slice(0, 10);
    if (cs > startStr && cs < todayStr) push(cs);
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 2, 0);
  }
  push(todayStr);

  return points;
}

/**
 * Sum of all `type === 'interest'` adjustments on a bucket (date ≤ today).
 */
export function getActualInterest(bucket, now = new Date()) {
  const todayStr = now.toISOString().slice(0, 10);
  return (bucket.adjustments || [])
    .filter(a => a.type === 'interest' && a.date <= todayStr)
    .reduce((s, a) => s + a.amount, 0);
}

/* ─── Bucket groups ────────────────────────────────────────────────────────── */

/** A bucket value in its own currency → main currency (latest rate; native if no rate). */
function toMain(bucket, native, mainCurrency) {
  if (!bucket.currency || bucket.currency === mainCurrency) return native;
  const r = getLatestRate(bucket.currency, mainCurrency);
  return r != null ? native * r : native;
}

/** Sum interest adjustments on a bucket with date in [fromStr, todayStr], in main currency. */
function bucketInterestSince(bucket, fromStr, todayStr, mainCurrency) {
  const native = (bucket.adjustments || [])
    .filter(a => a.type === 'interest' && a.date <= todayStr && (!fromStr || a.date >= fromStr))
    .reduce((s, a) => s + a.amount, 0);
  return toMain(bucket, native, mainCurrency);
}

/** Total interest across a set of buckets within [fromStr, toStr], in main currency. */
export function getGroupInterestBetween(buckets, fromStr, toStr, mainCurrency) {
  return (buckets || []).reduce(
    (s, b) => s + bucketInterestSince(b, fromStr || null, toStr, mainCurrency),
    0
  );
}

/**
 * Total interest earned across a set of buckets, in the app's main currency, for
 * several time windows: { total, ytd, y1, m6, m3 }.
 * @param {Bucket[]} buckets @param {string} mainCurrency @param {Date} [now]
 *
 * ponytail: latest FX rate for all windows, not per-adjustment-date rate —
 * upgrade to getRateForMonth if cross-currency interest drift matters.
 */
export function getGroupInterestWindows(buckets, mainCurrency, now = new Date()) {
  const todayStr = now.toISOString().slice(0, 10);
  const ytdStart = `${now.getFullYear()}-01-01`;
  const starts = {
    total: null,
    ytd:   ytdStart,
    y1:    lookbackStartDate('1Y', now).toISOString().slice(0, 10),
    m6:    lookbackStartDate('6M', now).toISOString().slice(0, 10),
    m3:    lookbackStartDate('3M', now).toISOString().slice(0, 10),
  };
  const out = { total: 0, ytd: 0, y1: 0, m6: 0, m3: 0 };
  (buckets || []).forEach(b => {
    for (const k of Object.keys(starts)) {
      out[k] += bucketInterestSince(b, starts[k], todayStr, mainCurrency);
    }
  });
  return out;
}

/**
 * Monthly interest across a set of buckets for the trailing `monthsBack` months,
 * in the app's main currency. Returns [{ name:'Mon YYYY', month:'YYYY-MM', interest }].
 */
export function getGroupMonthlyInterest(buckets, mainCurrency, monthsBack = 12, now = new Date()) {
  const byMonth = {};
  (buckets || []).forEach(b => {
    (b.adjustments || []).forEach(a => {
      if (a.type !== 'interest') return;
      const ym = (a.date || '').slice(0, 7);
      if (!ym) return;
      byMonth[ym] = (byMonth[ym] || 0) + toMain(b, a.amount, mainCurrency);
    });
  });
  const out = [];
  for (let i = monthsBack - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({
      month: ym,
      name: `${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`,
      interest: Math.round(byMonth[ym] || 0),
    });
  }
  return out;
}

/**
 * Summed main-currency balance history for a set of buckets over a lookback window.
 * Per-bucket series from getHistoricalBalanceSeries share the same sample dates
 * (derived from lookbackKey + now), so they zip by index. Foreign buckets
 * contribute balanceMain (falling back to native balance if no FX rate).
 * Returns [{ date, name, balance }].
 */
export function getGroupBalanceHistory(buckets, transactions, lookbackKey, mainCurrency, opts = {}) {
  const totals = {}; // date → { name, balance }
  (buckets || []).forEach(b => {
    const isForeign = b.currency && b.currency !== mainCurrency;
    const series = getHistoricalBalanceSeries(b, transactions, lookbackKey, {
      mainCurrency, start: opts.start, now: opts.now,
    });
    series.forEach(pt => {
      const val = isForeign ? (pt.balanceMain ?? pt.balance) : pt.balance;
      if (!totals[pt.date]) totals[pt.date] = { date: pt.date, name: pt.name, balance: 0 };
      totals[pt.date].balance += val || 0;
    });
  });
  return Object.values(totals)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(p => ({ ...p, balance: Math.round(p.balance) }));
}

/**
 * Infer an annual interest rate from a bucket's history.
 *
 * For each interest adjustment in the past 12 months (or all if the bucket has
 * fewer), compute the implied APY assuming it's a monthly periodic accrual:
 *   apy = (adjustment.amount / preAdjustmentBalance) * 12
 * Returns the mean APY as a decimal, or 0 when no data.
 */
export function inferAnnualInterestRate(bucket, transactions, now = new Date()) {
  const interestAdjs = (bucket.adjustments || []).filter(a => a.type === 'interest');
  if (interestAdjs.length === 0) return 0;

  const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
    .toISOString().slice(0, 10);
  const recent = interestAdjs.filter(a => a.date >= oneYearAgo);
  const list = recent.length > 0 ? recent : interestAdjs;

  const events = [];
  transactions.forEach(t => {
    if (t.type === 'savings' && t.bucket === bucket.id) {
      events.push({ date: t.date, amount: t.amount, kind: 'txn', id: t.id });
    }
  });
  (bucket.adjustments || []).forEach(a => {
    events.push({ date: a.date, amount: a.amount, kind: 'adj', id: a.id });
  });
  events.sort((a, b) =>
    a.date.localeCompare(b.date) || (a.id || '').localeCompare(b.id || '')
  );

  const rates = [];
  for (const adj of list) {
    let bal = bucket.initialBalance || 0;
    for (const e of events) {
      if (e.kind === 'adj' && e.id === adj.id) continue;
      if (e.date < adj.date) bal += e.amount;
    }
    if (bal > 0) rates.push((adj.amount / bal) * 12);
  }
  if (rates.length === 0) return 0;
  return rates.reduce((s, r) => s + r, 0) / rates.length;
}

export function monthsToGoal(balance, goal, monthly) {
  if (!monthly || monthly <= 0) return null;
  if (balance >= goal) return 0;
  return Math.ceil((goal - balance) / monthly);
}

/**
 * Sum of all non-archived buckets' monthlyTarget, expressed in the app's main
 * currency. Foreign buckets are converted using the latest cached FX rate;
 * if no rate is cached for a foreign bucket, that bucket's contribution is
 * skipped (rather than mixed in at the wrong scale).
 */
export function getPlannedMonthlySavings(buckets, mainCurrency) {
  return (buckets || []).filter(b => !b.archived).reduce((s, b) => {
    const t = Number(b.monthlyTarget) || 0;
    if (!t) return s;
    if (!b.currency || b.currency === mainCurrency) return s + t;
    const r = getLatestRate(b.currency, mainCurrency);
    return r != null ? s + t * r : s;
  }, 0);
}

/**
 * How many months of `year` a bucket is "active" for planning purposes:
 *   - If the bucket was created after the year, returns 0.
 *   - If created during the year, returns 12 minus the creation month index.
 *   - Otherwise (created before the year, not archived), returns 12.
 * Used so annual planned savings reflect the effective contribution window
 * (a bucket created in March only plans for 10 months that year, not 12).
 */
export function bucketActiveMonthsInYear(bucket, year) {
  if (!bucket) return 0;
  if (bucket.archived) return 0;
  if (!bucket.createdDate) return 12;
  const d = new Date(bucket.createdDate);
  const cy = d.getFullYear();
  if (cy > year) return 0;
  if (cy < year) return 12;
  return Math.max(0, 12 - d.getMonth());
}

/**
 * Effective annual planned savings — sum of (monthlyTarget × active-months) for
 * every non-archived bucket, in the app's main currency. Foreign buckets use
 * the latest cached FX rate; if no rate is cached, that bucket is skipped.
 */
export function getPlannedAnnualSavings(buckets, year, mainCurrency) {
  return (buckets || []).filter(b => !b.archived).reduce((s, b) => {
    const t = Number(b.monthlyTarget) || 0;
    if (!t) return s;
    const months = bucketActiveMonthsInYear(b, year);
    if (months === 0) return s;
    if (!b.currency || b.currency === mainCurrency) return s + t * months;
    const r = getLatestRate(b.currency, mainCurrency);
    return r != null ? s + t * months * r : s;
  }, 0);
}

/**
 * Per-month planned-vs-actual savings rate for a given year.
 *
 * planned% = plannedMonthlySavings / budgetedMonthlyIncome
 * actual%  = sum(savings txns in month) / sum(income txns in month)
 *            (falls back to budgetedMonthlyIncome when actual income is 0,
 *             so future months still render a sensible bar)
 *
 * Returns one entry per month: 12 entries, MONTH_SHORT names.
 */
export function getPlannedVsActualSavingsByMonth(
  transactions, buckets, categories, budgetItems, year, mainCurrency
) {
  const planned = getPlannedMonthlySavings(buckets, mainCurrency);
  const incomeCats = (categories || []).filter(c => c.type === 'income');
  return MONTH_SHORT.map((name, i) => {
    const ym = `${year}-${String(i + 1).padStart(2, '0')}`;
    const budgetedIncome = incomeCats.reduce((s, c) => {
      const b = (budgetItems || []).find(x => x.id === c.id);
      return s + resolveBudgetAmount(b, ym);
    }, 0);
    let actualIncome = 0;
    let actualSavings = 0;
    (transactions || []).forEach(t => {
      if (!t.date || !t.date.startsWith(ym)) return;
      if (t.type === 'income')  actualIncome  += Number(t.amount) || 0;
      if (t.type === 'savings') actualSavings += Number(t.amount) || 0;
    });
    const incomeForActual = actualIncome > 0 ? actualIncome : budgetedIncome;
    const plannedPct = budgetedIncome > 0
      ? Math.round((planned / budgetedIncome) * 1000) / 10
      : 0;
    const actualPct  = incomeForActual > 0
      ? Math.round((actualSavings / incomeForActual) * 1000) / 10
      : 0;
    return {
      name,
      plannedPct,
      actualPct,
      plannedAmt: planned,
      actualAmt:  actualSavings,
      budgetedIncome,
      actualIncome,
    };
  });
}

export function annualBudgetSummary(categories, budgetItems, yearlyExpenses, year) {
  const y = year || new Date().getFullYear();
  const months = Array.from({ length: 12 }, (_, i) =>
    `${y}-${String(i + 1).padStart(2, '0')}`
  );

  const income = categories
    .filter(c => c.type === 'income')
    .reduce((s, c) => {
      const b = budgetItems.find(x => x.id === c.id);
      return s + months.reduce((ms, m) => ms + resolveBudgetAmount(b, m), 0);
    }, 0);

  const expenses = categories
    .filter(c => c.type === 'expense')
    .reduce((s, c) => {
      const b = budgetItems.find(x => x.id === c.id);
      return s + months.reduce((ms, m) => ms + resolveBudgetAmount(b, m), 0);
    }, 0);

  const yearly = yearlyExpenses.reduce((s, ye) => s + (ye.amount || 0), 0);

  const savings = income - expenses - yearly;
  const rate = income > 0 ? Math.round((savings / income) * 100) : 0;

  return { income, expenses: expenses + yearly, monthlyExpenses: expenses / 12, yearlySum: yearly, savings, rate };
}

/* ─── Per-category analytics (Budget category detail view) ──────────────────── */

/** Step back `n` months from a 'YYYY-MM' key, returning the resulting 'YYYY-MM'. */
function addMonths(monthKey, delta) {
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(y, (m - 1) + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Rolling monthly actual-vs-budget series for a single category.
 * @param {Transaction[]} transactions
 * @param {object|undefined} budgetItem  matching budgetItems entry (id === categoryId)
 * @param {string} categoryId
 * @param {{ months?: number, endMonth?: string, type?: string }} [opts]
 * @returns {Array<{ month: string, label: string, actual: number, budget: number, pct: number|null }>}
 */
export function getCategoryMonthlySeries(transactions, budgetItem, categoryId, opts = {}) {
  const { months = 12, type = 'expense' } = opts;
  const endMonth = opts.endMonth || new Date().toISOString().slice(0, 7);

  // Sum actuals per month for this category/type.
  const byMonth = {};
  (transactions || []).forEach(t => {
    if (t.category === categoryId && t.type === type) {
      const mk = t.date.slice(0, 7);
      byMonth[mk] = (byMonth[mk] || 0) + (t.amount || 0);
    }
  });

  const out = [];
  for (let i = months - 1; i >= 0; i--) {
    const mk = addMonths(endMonth, -i);
    const actual = byMonth[mk] || 0;
    const budget = resolveBudgetAmount(budgetItem, mk);
    out.push({
      month: mk,
      label: MONTH_SHORT[Number(mk.slice(5, 7)) - 1],
      actual,
      budget,
      pct: budget > 0 ? Math.round((actual / budget) * 100) : null,
    });
  }
  return out;
}

function medianOf(sortedAsc) {
  const n = sortedAsc.length;
  if (!n) return 0;
  const mid = Math.floor(n / 2);
  return n % 2 ? sortedAsc[mid] : (sortedAsc[mid - 1] + sortedAsc[mid]) / 2;
}

/**
 * Flag unusually large transactions using a robust modified z-score
 * (median + MAD), which — unlike mean+stdev — isn't masked by the outlier
 * itself in small samples. Only large deviations are flagged.
 * @returns {Transaction[]} sorted by amount desc
 */
export function detectOutlierTransactions(txns, { threshold = 3.5 } = {}) {
  const amounts = (txns || []).map(t => t.amount || 0);
  if (amounts.length < 4) return [];
  const median = medianOf([...amounts].sort((a, b) => a - b));
  const mad = medianOf(amounts.map(a => Math.abs(a - median)).sort((a, b) => a - b));
  if (mad === 0) return []; // amounts essentially uniform
  return (txns || [])
    .filter(t => (0.6745 * ((t.amount || 0) - median)) / mad > threshold)
    .sort((a, b) => (b.amount || 0) - (a.amount || 0));
}

/**
 * Group a category's transactions by normalized description; surface those that
 * recur across ≥3 distinct months (likely subscriptions / standing costs).
 * @returns {Array<{ description: string, count: number, months: number, avgAmount: number }>}
 */
export function detectRepetitiveExpenses(txns) {
  const groups = {};
  (txns || []).forEach(t => {
    const key = (t.description || '').trim().toLowerCase();
    if (!key) return;
    if (!groups[key]) groups[key] = { description: t.description.trim(), total: 0, count: 0, months: new Set() };
    const g = groups[key];
    g.total += t.amount || 0;
    g.count += 1;
    g.months.add((t.date || '').slice(0, 7));
  });
  return Object.values(groups)
    .filter(g => g.months.size >= 3)
    .map(g => ({ description: g.description, count: g.count, months: g.months.size, avgAmount: g.total / g.count }))
    .sort((a, b) => b.months - a.months || b.avgAmount - a.avgAmount);
}

/** Round to a sensible step for budget suggestions (nearest 10 under 500, else nearest 50). */
function roundBudget(v) {
  if (v <= 0) return 0;
  const step = v < 500 ? 10 : 50;
  return Math.round(v / step) * step;
}

/**
 * Recommend right-sizing a category's monthly budget to its recent actuals.
 * Averages `actual` over months from the first month that had any spend (so a
 * newly-created category isn't dragged down by leading zeros).
 * @param {Array<{actual:number}>} series  from getCategoryMonthlySeries
 * @param {number} currentBudget
 * @returns {{ action: 'lower'|'raise'|'ok', avg: number, suggested: number, monthsOver: number }}
 */
export function recommendCategoryBudget(series, currentBudget) {
  const firstIdx = series.findIndex(s => s.actual > 0);
  const active = firstIdx === -1 ? [] : series.slice(firstIdx);
  const avg = active.length ? active.reduce((s, m) => s + m.actual, 0) / active.length : 0;
  const monthsOver = series.filter(s => s.budget > 0 && s.actual > s.budget).length;

  let action = 'ok';
  if (currentBudget > 0 && active.length) {
    if (avg <= currentBudget * 0.85) action = 'lower';
    else if (avg >= currentBudget * 1.10) action = 'raise';
  }
  return { action, avg, suggested: roundBudget(avg), monthsOver };
}
